'use client'
import { SearchType, searchTypes, mobileSearchTypes } from '@/app/(site)/search/types'
import { getChannelGifsUrl, getChannelUrl } from '@/app/components/collections/urls'
import { ChannelGifsOptions } from '@/app/util/channel-api'
import { publicRuntimeConfig } from '@/app/util/env'
import { processSearchTerm } from '@/app/util/search-term'
import { useGiphyFetch } from '@/app/util/use-gf'
import { useClientSideAPIKey } from '@/util/url'
import { Result, SearchOptions } from '@giphy/js-fetch-api'
import { IChannel, IGif } from '@giphy/js-types'
import { useParams, useSearchParams } from 'next/navigation'
import useSWR from 'swr'
import useSWRImmutable from 'swr/immutable'
import { GenericResult } from 'ui/src/hooks/use-fetch-data'

const apiUrlV2 = publicRuntimeConfig.apiUrl.replace('v1', 'v2')

type TrendingSearch = { name: string; analytics_response_payload: string }

const getParams = (obj: any) => new URLSearchParams(obj).toString()

export function useGif(id?: string) {
    const gf = useGiphyFetch()
    return useSWR<IGif>(id ? id : null, (id: string) => gf.gif(id).then((res) => res.data))
}

export function useSearchType(): SearchType {
    const params = useParams()

    // search params are on the homepage: ?type=stickers
    // add emoji, memes, reactions, animals, artists logic to populate grid on mobile homepage
    // there wont be search capability for these types.
    const searchParams = useSearchParams()
    const searchParamType = searchParams.get('type')?.toLowerCase() as any
    if (mobileSearchTypes.includes(searchParamType)) {
        return searchParamType
    }
    // params are on search pages: pizza-stickers
    const [, type] = params.term ? processSearchTerm(params.term as string) : []
    if (searchTypes.includes(type as any)) {
        return type as SearchType
    }
    return 'gifs'
}

export function useImmutableChannel(channelId: number | string | null = null) {
    // we will load an immutable channel and apply mutations through the SWR mutate api
    return useSWRImmutable<IChannel>(channelId ? getChannelUrl(channelId) : null)
}

export function useChannelWithFallback(channel: IChannel) {
    return useSWRImmutable<IChannel>(getChannelUrl(channel.id), null, {
        fallback: { [getChannelUrl(channel.id)]: channel },
    })
}

export function useChannelGifs(channelId: number | string | null = null, options: ChannelGifsOptions) {
    return useSWRImmutable<IChannel>(channelId ? getChannelGifsUrl(channelId, options) : null)
}

export const useTrendingSearches = (term: string = '', options: SearchOptions = {}) =>
    useSWR<GenericResult<TrendingSearch>>(
        `${apiUrlV2}trending/searches?${getParams({
            ...options,
            q: term,
            api_key: useClientSideAPIKey(),
        })}`
    )

export const useSearchChannel = (channelName: string, options: SearchOptions = {}) => {
    const api_key = useClientSideAPIKey()
    return useSWR<GenericResult<IChannel>>(
        channelName
            ? `${publicRuntimeConfig.apiUrl}channels/search?${getParams({
                  ...options,
                  q: channelName.replace('@', ''),
                  api_key,
              })}`
            : null
    )
}

export const useSearchChannelByTerm = (term: string, options: SearchOptions = {}) =>
    useSWR<GenericResult<IChannel>>(
        `${publicRuntimeConfig.apiUrl}channels/search?${getParams({
            ...options,
            q: term,
            api_key: useClientSideAPIKey(),
        })}`
    )

type SearchName = { analytics_response_payload: string; name: string; response_id: string }
type SearchTagsResult = { data: SearchName[] } & Result
export const useSearchTags = (term: string, options: SearchOptions = {}) =>
    useSWR<SearchTagsResult>(
        `${publicRuntimeConfig.apiUrl}gifs/search/tags?${getParams({
            ...options,
            q: term,
            api_key: useClientSideAPIKey(),
        })}`
    )
